/**
 * Adapted from PlayCanvas Engine's gsplat-varyings.js and its GLSL/WGSL varying chunks at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type {
  GSplatMaterialLeaf,
  GSplatShaderLanguage,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSPLAT_PROJECTION_CACHE_BASE_WORDS } from './GSplatProjectorConstants'

export type GSplatVaryingType = 'float32' | 'int32' | 'uint32'

export interface GSplatVaryingDescriptor {
  readonly name: string
  readonly type: GSplatVaryingType
  readonly components: 1 | 2 | 3 | 4
}

export type GSplatVaryingMaterial = Pick<
  GSplatMaterialLeaf,
  'revision' | 'getShaderChunks' | 'getDefine' | 'setDefine' | 'update'
>

export interface GSplatVaryingChunks {
  readonly gsplatUserVaryingsVS: string
  readonly gsplatUserVaryingsPS: string
  readonly gsplatUserVaryingsFlushVS?: string
  readonly gsplatUserVaryingsCS?: string
  readonly gsplatUserCacheWriteCS?: string
  readonly gsplatUserCacheReadVS?: string
}

const GLSL_TYPES: Readonly<Record<GSplatVaryingType, readonly string[]>> = Object.freeze({
  float32: Object.freeze(['float', 'vec2', 'vec3', 'vec4']),
  int32: Object.freeze(['int', 'ivec2', 'ivec3', 'ivec4']),
  uint32: Object.freeze(['uint', 'uvec2', 'uvec3', 'uvec4']),
})

const WGSL_TYPES: Readonly<Record<GSplatVaryingType, readonly string[]>> = Object.freeze({
  float32: Object.freeze(['f32', 'vec2f', 'vec3f', 'vec4f']),
  int32: Object.freeze(['i32', 'vec2i', 'vec3i', 'vec4i']),
  uint32: Object.freeze(['u32', 'vec2u', 'vec3u', 'vec4u']),
})

const COMPONENT_SWIZZLE = Object.freeze(['x', 'y', 'z', 'w'])
const IDENTIFIER_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/

const GLSL_CHUNK_NAMES = Object.freeze(['gsplatUserVaryingsVS', 'gsplatUserVaryingsPS'])
const WGSL_CHUNK_NAMES = Object.freeze([
  'gsplatUserVaryingsVS',
  'gsplatUserVaryingsFlushVS',
  'gsplatUserVaryingsCS',
  'gsplatUserVaryingsPS',
  'gsplatUserCacheWriteCS',
  'gsplatUserCacheReadVS',
])

function pascal(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1)
}

function encodeWord(type: GSplatVaryingType, expression: string): string {
  return type === 'uint32' ? expression : `bitcast<u32>(${expression})`
}

function decodeWord(type: GSplatVaryingType, expression: string): string {
  if (type === 'uint32') return expression
  return type === 'int32' ? `bitcast<i32>(${expression})` : `bitcast<f32>(${expression})`
}

function freezeDescriptor(descriptor: GSplatVaryingDescriptor): GSplatVaryingDescriptor {
  return Object.freeze({
    name: descriptor.name,
    type: descriptor.type,
    components: descriptor.components,
  })
}

interface AppliedState {
  readonly varyingVersion: number
  readonly materialRevision: number
  readonly language: GSplatShaderLanguage
}

/** Ordered custom varying registry 与双语言 chunk generator。 */
export class GSplatVaryings {
  private streamList: readonly GSplatVaryingDescriptor[] = Object.freeze([])
  private wordCount = 0
  private currentVersion = 0
  private readonly applied = new WeakMap<GSplatVaryingMaterial, AppliedState>()

  get streams(): readonly GSplatVaryingDescriptor[] {
    return this.streamList
  }

  get words(): number {
    return this.wordCount
  }

  get version(): number {
    return this.currentVersion
  }

  /** Hybrid projector/cache consumer 必须使用同一 stride。 */
  get projectionCacheStrideWords(): number {
    return GSPLAT_PROJECTION_CACHE_BASE_WORDS + this.wordCount
  }

  get cacheLayoutVersion(): number {
    return this.currentVersion
  }

  add(streams: readonly GSplatVaryingDescriptor[]): void {
    if (!Array.isArray(streams) || streams.length === 0) {
      throw new Error('GSplatVaryings.add 要求非空 descriptor 数组')
    }

    const existing = new Set(this.streamList.map((stream) => stream.name))
    const batch = new Set<string>()
    const staged: GSplatVaryingDescriptor[] = []
    for (const stream of streams) {
      if (!stream || !IDENTIFIER_PATTERN.test(stream.name ?? '')) {
        throw new Error(`非法 GSplat varying name: "${stream?.name}"`)
      }
      if (!(stream.type in GLSL_TYPES)) {
        throw new Error(`GSplat varying "${stream.name}" 使用了不支持的 type`)
      }
      if (!Number.isInteger(stream.components) || stream.components < 1 || stream.components > 4) {
        throw new Error(`GSplat varying "${stream.name}" components 必须为 1..4`)
      }
      if (existing.has(stream.name) || batch.has(stream.name)) {
        throw new Error(`GSplat varying "${stream.name}" 已存在`)
      }
      batch.add(stream.name)
      staged.push(freezeDescriptor(stream))
    }

    this.streamList = Object.freeze([...this.streamList, ...staged])
    this.changed()
  }

  remove(names: readonly string[]): void {
    if (!Array.isArray(names) || names.length === 0) {
      throw new Error('GSplatVaryings.remove 要求非空 name 数组')
    }
    const removed = new Set(names)
    const next = this.streamList.filter((stream) => !removed.has(stream.name))
    if (next.length === this.streamList.length) return
    this.streamList = Object.freeze(next)
    this.changed()
  }

  generateChunks(language: GSplatShaderLanguage): GSplatVaryingChunks {
    const types = language === 'wgsl' ? WGSL_TYPES : GLSL_TYPES
    const vertex: string[] = []
    const fragment: string[] = []
    const flush: string[] = []
    const compute: string[] = []
    const cacheWrite: string[] = []
    const cacheRead: string[] = []

    let wordOffset = 0
    for (const stream of this.streamList) {
      const shaderType = types[stream.type][stream.components - 1]
      const functionName = pascal(stream.name)
      if (language === 'glsl') {
        vertex.push(
          `\nflat varying ${shaderType} user_${stream.name};\n` +
            `void set${functionName}(${shaderType} value) { user_${stream.name} = value; }\n`
        )
        fragment.push(
          `\nflat varying ${shaderType} user_${stream.name};\n` +
            `${shaderType} get${functionName}() { return user_${stream.name}; }\n`
        )
      } else {
        vertex.push(
          `\nvarying @interpolate(flat) user_${stream.name}: ${shaderType};\n` +
            `var<private> _user_${stream.name}: ${shaderType};\n` +
            `fn set${functionName}(value: ${shaderType}) { _user_${stream.name} = value; }\n`
        )
        fragment.push(
          `\nvarying @interpolate(flat) user_${stream.name}: ${shaderType};\n` +
            `fn get${functionName}() -> ${shaderType} { return user_${stream.name}; }\n`
        )
        flush.push(`\noutput.user_${stream.name} = _user_${stream.name};\n`)
        compute.push(
          `\nvar<private> _user_${stream.name}: ${shaderType};\n` +
            `fn set${functionName}(value: ${shaderType}) { _user_${stream.name} = value; }\n`
        )

        const words: string[] = []
        for (let componentIndex = 0; componentIndex < stream.components; componentIndex++) {
          const component =
            stream.components === 1
              ? `_user_${stream.name}`
              : `_user_${stream.name}.${COMPONENT_SWIZZLE[componentIndex]}`
          const cacheWord = GSPLAT_PROJECTION_CACHE_BASE_WORDS + wordOffset + componentIndex
          cacheWrite.push(
            `\nprojCache[base + ${cacheWord}u] = ${encodeWord(stream.type, component)};\n`
          )
          words.push(decodeWord(stream.type, `projCache[base + ${cacheWord}u]`))
        }
        const value = stream.components === 1 ? words[0] : `${shaderType}(${words.join(', ')})`
        cacheRead.push(`\noutput.user_${stream.name} = ${value};\n`)
      }
      wordOffset += stream.components
    }

    const result: GSplatVaryingChunks = {
      gsplatUserVaryingsVS: vertex.join(''),
      gsplatUserVaryingsPS: fragment.join(''),
    }
    if (language === 'glsl') return Object.freeze(result)
    return Object.freeze({
      ...result,
      gsplatUserVaryingsFlushVS: flush.join(''),
      gsplatUserVaryingsCS: compute.join(''),
      gsplatUserCacheWriteCS: cacheWrite.join(''),
      gsplatUserCacheReadVS: cacheRead.join(''),
    })
  }

  /** 对单个 material 原子发布当前语言的全部 managed chunks。 */
  apply(material: GSplatVaryingMaterial, language: GSplatShaderLanguage): void {
    const previousApplied = this.applied.get(material)
    if (
      previousApplied?.varyingVersion === this.currentVersion &&
      previousApplied.materialRevision === material.revision &&
      previousApplied.language === language
    ) {
      return
    }

    const chunks = material.getShaderChunks(language)
    const managedNames = language === 'wgsl' ? WGSL_CHUNK_NAMES : GLSL_CHUNK_NAMES
    const previousChunks = new Map<string, string | undefined>()
    for (const name of managedNames) previousChunks.set(name, chunks.get(name))
    const previousDefine = material.getDefine('GSPLAT_USER_VARYINGS')
    const generated = this.streamList.length > 0 ? this.generateChunks(language) : null

    try {
      material.setDefine('GSPLAT_USER_VARYINGS', this.streamList.length > 0)
      if (generated) {
        for (const name of managedNames) {
          const source = generated[name as keyof GSplatVaryingChunks]
          if (source !== undefined) chunks.set(name, source)
        }
      } else {
        for (const name of managedNames) chunks.delete(name)
      }
      material.update()
      this.applied.set(material, {
        varyingVersion: this.currentVersion,
        materialRevision: material.revision,
        language,
      })
    } catch (error) {
      for (const [name, source] of previousChunks) {
        if (source === undefined) chunks.delete(name)
        else chunks.set(name, source)
      }
      try {
        material.setDefine('GSPLAT_USER_VARYINGS', previousDefine)
      } catch {
        // 保留原始 compile/publish 错误。
      }
      throw error
    }
  }

  private changed(): void {
    this.wordCount = this.streamList.reduce((sum, stream) => sum + stream.components, 0)
    this.currentVersion++
  }
}
